import { Check, Minus, TriangleAlert, Info } from 'lucide-react';

/**
 * DividripOpinionBanner — renders the server-computed Buy / Neutral /
 * Avoid verdict (sourced from `/stocks/{ticker}/triangle` response's
 * `opinion` field) inside a single attention-grabbing banner. Used on
 * the Triangle Tab inside the Stock Modal AND on the standalone
 * `/triangle/[ticker]` + `/triangle/compare` pages.
 *
 * Round 92o++++++++N++++++++++ (Jun 2026) — Educational signal only.
 * The disclaimer text is always rendered as the last line so the
 * legal posture is unmissable. Three tiers by design — we're not
 * investment advisors and finer granularity invites scrutiny we
 * don't want.
 */
const VERDICT_STYLES = {
  buy: {
    ring: 'border-emerald-400 dark:border-emerald-700',
    bg: 'bg-emerald-50 dark:bg-emerald-950',
    tag: 'bg-emerald-600 text-white',
    tagText: 'Would Buy',
    Icon: Check,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    headlineColor: 'text-emerald-900 dark:text-emerald-100',
  },
  consider: {
    ring: 'border-lime-400 dark:border-lime-700',
    bg: 'bg-lime-50 dark:bg-lime-950',
    tag: 'bg-lime-600 text-white',
    tagText: 'Consider',
    Icon: Check,
    iconColor: 'text-lime-600 dark:text-lime-400',
    headlineColor: 'text-lime-900 dark:text-lime-100',
  },
  neutral: {
    ring: 'border-slate-300 dark:border-slate-600',
    bg: 'bg-slate-50 dark:bg-slate-900',
    tag: 'bg-slate-500 text-white',
    tagText: 'Neutral',
    Icon: Minus,
    iconColor: 'text-slate-500 dark:text-slate-400',
    headlineColor: 'text-slate-900 dark:text-slate-100',
  },
  caution: {
    ring: 'border-yellow-400 dark:border-yellow-600',
    bg: 'bg-yellow-50 dark:bg-yellow-950',
    tag: 'bg-yellow-500 text-white',
    tagText: 'Caution',
    Icon: TriangleAlert,
    iconColor: 'text-yellow-600 dark:text-yellow-400',
    headlineColor: 'text-yellow-900 dark:text-yellow-100',
  },
  avoid: {
    ring: 'border-rose-400 dark:border-rose-700',
    bg: 'bg-rose-50 dark:bg-rose-950',
    tag: 'bg-rose-600 text-white',
    tagText: 'Would Not Buy',
    Icon: TriangleAlert,
    iconColor: 'text-rose-600 dark:text-rose-400',
    headlineColor: 'text-rose-900 dark:text-rose-100',
  },
};

const ASSET_LABELS = {
  stock: 'Dividend Stock',
  reit: 'REIT',
  bdc: 'BDC',
  cef: 'Closed-End Fund',
  etf: 'ETF',
  mlp: 'MLP',
};

export default function DividripOpinionBanner({ opinion, compact = false }) {
  if (!opinion || !opinion.verdict) return null;

  const style = VERDICT_STYLES[opinion.verdict] || VERDICT_STYLES.neutral;
  const { Icon } = style;
  const assetLabel = ASSET_LABELS[opinion.asset_class] || 'Investment';

  return (
    <div
      className={`relative rounded-xl border-2 ${style.ring} ${style.bg} p-3 ${compact ? '' : 'sm:p-4'} shadow-sm`}
      data-testid={`dividrip-opinion-${opinion.verdict}`}
    >
      {/* Title — bold "DiviDrip's Opinion" header above the verdict
          pill so users immediately recognise whose opinion they're
          reading. Round 95y (Jun 2026) — sized down a tier (text-xl
          → text-lg) to reduce the modal scroll-distance to the
          radar. */}
      <h3 className="text-base sm:text-lg font-bold tracking-tight mb-1.5" data-testid="dividrip-opinion-title">
        DiviDrip's Opinion
      </h3>

      {/* Verdict tag + asset class chip row */}
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        <span
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.08em] ${style.tag}`}
          data-testid="dividrip-opinion-verdict-tag"
        >
          <Icon className="w-3.5 h-3.5" />
          {style.tagText}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground px-2 py-0.5 rounded-full border border-border bg-background/60">
          {assetLabel}
        </span>
      </div>

      {/* Headline */}
      {opinion.headline && (
        <p className={`text-xs font-bold ${style.headlineColor} mb-1.5`} data-testid="dividrip-opinion-headline">
          {opinion.headline}
        </p>
      )}

      {/* Bullet reasons (max 4) */}
      {Array.isArray(opinion.bullets) && opinion.bullets.length > 0 && (
        <ul className="space-y-1 mb-2" data-testid="dividrip-opinion-bullets">
          {opinion.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-[11px] text-foreground">
              <span className={`mt-1 w-1 h-1 rounded-full shrink-0 ${style.iconColor.replace('text-', 'bg-')}`} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Round P133 (Feb 2026) — Signal breakdown accordion.
          The old opinion was a black box: verdict + 2-3 bullets and
          that was it. Now we surface the FULL signal ledger so the
          user can see exactly which metrics dragged the verdict up or
          down. Only rendered when the server sends the P133 payload
          (older `/triangle` responses without the new fields render
          identically to before). */}
      {(Array.isArray(opinion.negative_signals) || Array.isArray(opinion.positive_signals)) && (
        <details className="mt-1 mb-2 group" data-testid="dividrip-opinion-signal-breakdown">
          <summary className="text-[11px] font-bold cursor-pointer select-none flex items-center gap-1.5 text-muted-foreground hover:text-foreground">
            <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
            Show all signals
            {typeof opinion.signal_count === 'number' && (
              <span className="text-[10px] font-normal opacity-70">
                ({opinion.signal_count} data point{opinion.signal_count === 1 ? '' : 's'}
                {typeof opinion.score_normalized === 'number' ? `, score ${opinion.score_normalized >= 0 ? '+' : ''}${opinion.score_normalized.toFixed(2)}` : ''})
              </span>
            )}
          </summary>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
            {Array.isArray(opinion.positive_signals) && opinion.positive_signals.length > 0 && (
              <div className="rounded-md bg-emerald-50/60 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-800/50 p-2" data-testid="dividrip-opinion-positive-signals">
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-emerald-700 dark:text-emerald-400 mb-1">Positive signals</p>
                <ul className="space-y-1">
                  {opinion.positive_signals.map((s, i) => (
                    <li key={i} className="flex items-baseline justify-between gap-2">
                      <span className="text-emerald-900 dark:text-emerald-200">
                        {s.label} {s.score >= 2 && <span className="text-[9px] font-bold ml-0.5 uppercase">strong</span>}
                      </span>
                      <span className="font-mono font-bold text-emerald-700 dark:text-emerald-300 shrink-0">{s.display}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {Array.isArray(opinion.negative_signals) && opinion.negative_signals.length > 0 && (
              <div className="rounded-md bg-rose-50/60 dark:bg-rose-950/30 border border-rose-200/60 dark:border-rose-800/50 p-2" data-testid="dividrip-opinion-negative-signals">
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-rose-700 dark:text-rose-400 mb-1">Negative signals</p>
                <ul className="space-y-1">
                  {opinion.negative_signals.map((s, i) => (
                    <li key={i} className="flex items-baseline justify-between gap-2">
                      <span className="text-rose-900 dark:text-rose-200">
                        {s.label} {s.score <= -2 && <span className="text-[9px] font-bold ml-0.5 uppercase">strong</span>}
                      </span>
                      <span className="font-mono font-bold text-rose-700 dark:text-rose-300 shrink-0">{s.display}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {Array.isArray(opinion.neutral_signals) && opinion.neutral_signals.length > 0 && (
              <div className="rounded-md bg-slate-50/60 dark:bg-slate-950/30 border border-slate-200/60 dark:border-slate-800/50 p-2 sm:col-span-2" data-testid="dividrip-opinion-neutral-signals">
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-600 dark:text-slate-400 mb-1">Neutral signals</p>
                <ul className="flex flex-wrap gap-x-4 gap-y-1">
                  {opinion.neutral_signals.map((s, i) => (
                    <li key={i} className="text-slate-700 dark:text-slate-300">
                      <span>{s.label}</span>
                      <span className="font-mono font-bold ml-1.5">{s.display}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </details>
      )}

      {/* What changes our mind */}
      {opinion.changes_our_mind && (
        <div className="flex items-start gap-2 rounded-md bg-background/60 dark:bg-background/40 border border-border/60 px-2.5 py-1.5 mb-1.5" data-testid="dividrip-opinion-changes">
          <Info className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
          <p className="text-[11px] text-foreground">
            <span className="font-bold">What changes our mind:</span> {opinion.changes_our_mind}
          </p>
        </div>
      )}

      {/* Disclaimer — matches the site-wide footer disclaimer
          aesthetic: amber/yellow bar with brick-red amber text inside
          a red border. Round 95y — text bumped 1 tier smaller and
          vertical padding tightened to recover modal real-estate. */}
      {opinion.disclaimer && (
        <p
          className="text-[11px] font-medium italic bg-yellow-200 dark:bg-yellow-300 text-amber-900 px-2.5 py-1.5 rounded leading-relaxed border-2 border-red-400 dark:border-red-600"
          data-testid="dividrip-opinion-disclaimer"
        >
          {opinion.disclaimer}
        </p>
      )}
    </div>
  );
}
